import { useState, type FC } from "react";

import { FormattedMessage } from "react-intl";
import { GKButton, GKCardImg, GKLoading, GKLoadingType } from "@granular/gds";
import { useNavigate } from "react-router-dom";
import { LoginCardLayout } from "../../LoginCardLayout";
import {
  identifyAccount,
  InitializedAccount,
  clearAllAccounts,
} from "../../../services/auth/new-msal/init";
import { getPhotoURL } from "../../../services/auth/new-msal/photo";
import { useMount } from "../../../hooks/mount";
import styles from "./AutoIdFlow.module.css";
import { doLoginRedirect } from "../../../services/auth/new-msal/redirect";

export const AutoIdFlow: FC = () => {
  const navigate = useNavigate();
  const [initializedAccount, setInitializedAccount] =
    useState<InitializedAccount>();
  const [photoURL, setPhotoURL] = useState<string>();

  useMount(() => {
    void (async () => {
      // Check if user just completed invitation - if so, go directly to manual identify
      const searchParams = new URLSearchParams(globalThis.location.search);
      const fromInvite = searchParams.get("fromInvite");

      const redirectToIdentify = () =>
        navigate(`/login/identify${globalThis.location.search}`);

      // If user just completed invitation, redirect immediately to force manual login
      // This ensures they must explicitly sign in rather than continuing with cached credentials
      if (fromInvite === "true") {
        console.log(
          "[MSAL] User completed invitation, redirecting to manual login",
        );
        redirectToIdentify();
        return;
      }

      // If we haven't identified the user in 5 seconds, redirect to manual identify
      const timeoutHandle = setTimeout(redirectToIdentify, 5000);
      let account: InitializedAccount | undefined;

      try {
        account = await identifyAccount(false);
        clearTimeout(timeoutHandle);
      } catch (error) {
        clearTimeout(timeoutHandle);
        console.warn("Error during auto-identify:", error);
        redirectToIdentify();
        return;
      }
      // We weren't able to auto-identify the user, so redirect to manual identify
      if (account === undefined) {
        redirectToIdentify();
        return;
      }

      setInitializedAccount(account);

      try {
        setPhotoURL(await getPhotoURL(account.authApp, account.authSource));
      } catch (error) {
        console.warn("Failed to load profile photo:", error);
      }
    })();
  });

  const loginAsIdentified = () => {
    if (!initializedAccount) return;

    // Note: The redirectUri parameter in performLoginRedirect is currently unused.
    // Post-login navigation is handled via sessionStorage (POST_LOGIN_REDIRECT key).
    // See containerHooks.ts for the actual redirect logic.
    void doLoginRedirect(
      initializedAccount.authApp,
      initializedAccount.authConfig,
      initializedAccount.account,
    );
  };

  const signInWithDifferentAccount = async () => {
    // Clear all cached accounts to force account selection
    await clearAllAccounts();

    // Navigate to the manual identification flow
    // The SelfIdFlow will force account selection by passing undefined as activeAccount
    navigate("/login/identify");
  };

  if (!initializedAccount) {
    return (
      <LoginCardLayout>
        <GKLoading
          className="pr-1"
          isLoading={true}
          type={GKLoadingType.Spinner}
          spinnerProps={{ width: 32 }}
        />
      </LoginCardLayout>
    );
  }

  return (
    <LoginCardLayout>
      <div className={styles.autoIdB2bAvatarContainer}>
        {photoURL ? (
          <GKCardImg
            className={`${styles.autoIdB2bAvatarContainerImg}`}
            src={photoURL}
            alt={initializedAccount.account.name ?? ""}
            data-qa={"profile-photo"}
          />
        ) : (
          <div
            className={`${styles.autoIdB2bAvatarContainerImg} ${styles.autoIdB2bAvatarPlaceholder}`}
            data-qa={"profile-photo-placeholder"}
          />
        )}
        <p>{initializedAccount.account.username}</p>
      </div>
      <GKButton onClick={loginAsIdentified} color="primary">
        <FormattedMessage id="continue-as" /> {initializedAccount.account.name}
      </GKButton>

      <GKButton
        className="mt-4"
        color="link"
        onClick={() => void signInWithDifferentAccount()}
      >
        <FormattedMessage id="sign-in-with-another-account" />
      </GKButton>
    </LoginCardLayout>
  );
};
